/**
 * F31 (Wave 2B) — admin per-user skill-ratings page.
 *
 * Route: `/users/:userId/skill-ratings`
 * Permission: `users:read` for the page (mirrors the user-detail surface); the
 * Override action is additionally gated on `skill_ratings:override` (super_admin
 * only — the button is hidden otherwise).
 *
 * Read-only summary of per-sport ratings (value + source + matches + W/L/D)
 * with an Override button per row that opens RatingOverrideDialog. Override
 * posts to `POST /admin/users/:userId/skill-ratings/:sportName/override`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  EmptyState,
  SkillLevelBadge,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { AdminRatingOverrideInput, SkillRatingSummary } from '@bravemove/types';
import type { SportName } from '@bravemove/design-tokens/icons';
import {
  adminSkillDisputesApi,
  type AdminUserSkillRatingsResponse,
} from '../../../api/skillDisputes';
import { RatingOverrideDialog } from '../../../components/skill/RatingOverrideDialog';
import { translateApiError } from '../../../auth/errors';
import { useAuth } from '../../../auth/useAuth';

export function AdminUserSkillRatingsPage() {
  const { t } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const canOverride = auth.adminUser?.permissions.includes('skill_ratings:override') ?? false;

  const [data, setData] = useState<AdminUserSkillRatingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<SkillRatingSummary | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await adminSkillDisputesApi.userSkillRatings(userId));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const handleOverride = async (input: AdminRatingOverrideInput): Promise<void> => {
    if (!userId || !overrideTarget) return;
    setOverrideError(null);
    try {
      await adminSkillDisputesApi.overrideRating(userId, overrideTarget.sport as SportName, input);
      setOverrideTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:skill.user_ratings.override_cta', { defaultValue: 'Rating overridden' }),
      });
      await refresh();
    } catch (err) {
      setOverrideError(translateApiError(err, t));
    }
  };

  const columns = useMemo<readonly TableColumn<SkillRatingSummary>[]>(
    () => [
      {
        key: 'sport',
        header: t('admin:skill.user_ratings.column.sport', { defaultValue: 'Sport' }),
        cell: (row) => (
          <span className="text-[13px] text-ink capitalize">{row.sport.replace(/-/g, ' ')}</span>
        ),
      },
      {
        key: 'rating',
        header: t('admin:skill.user_ratings.column.rating', { defaultValue: 'Rating' }),
        cell: (row) => (
          <SkillLevelBadge
            value={row.effectiveSkillValue}
            size="sm"
            confidenceTier={row.confidenceTier}
          />
        ),
      },
      {
        key: 'source',
        header: t('admin:skill.user_ratings.column.source', { defaultValue: 'Source' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t(`admin:skill.user_ratings.source.${row.skillSource}`, {
              defaultValue: row.skillSource,
            })}
          </span>
        ),
      },
      {
        key: 'matches',
        header: t('admin:skill.user_ratings.column.matches', { defaultValue: 'Matches' }),
        align: 'end',
        cell: (row) => (
          <span className="font-mono text-[12.5px] text-ink-2 tabular-nums">
            {row.matchesPlayed}
          </span>
        ),
      },
      {
        key: 'record',
        header: t('admin:skill.user_ratings.column.record', { defaultValue: 'W/L/D' }),
        align: 'end',
        cell: (row) => (
          <span className="font-mono text-[12px] text-ink-3 tabular-nums">
            {row.wins}/{row.losses}/{row.draws}
          </span>
        ),
      },
      ...(canOverride
        ? [
            {
              key: 'actions',
              header: '',
              align: 'end' as const,
              cell: (row: SkillRatingSummary) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setOverrideError(null);
                    setOverrideTarget(row);
                  }}
                  data-testid={`override-${row.sport}`}
                >
                  {t('admin:skill.user_ratings.override_cta', { defaultValue: 'Override' })}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, canOverride],
  );

  return (
    <div className="space-y-6" data-testid="user-skill-ratings-page">
      <div className="space-y-1">
        <h1 className="text-h1 font-bold text-ink">
          {t('admin:skill.user_ratings.page_title', { defaultValue: 'Skill ratings' })}
        </h1>
        <p className="text-[13px] text-ink-3">
          {data?.displayName ?? userId}
          {' · '}
          {t('admin:skill.user_ratings.page_subtitle', {
            defaultValue: 'Per-sport ratings + history for this player.',
          })}
        </p>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="user-skill-ratings-loading">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !data || data.ratings.length === 0 ? (
        <EmptyState
          title={t('admin:skill.user_ratings.empty', {
            defaultValue: 'This player has no rated sports yet.',
          })}
        />
      ) : (
        <Table
          data={data.ratings as SkillRatingSummary[]}
          columns={columns as TableColumn<SkillRatingSummary>[]}
          rowKey={(row) => row.sport}
        />
      )}

      {overrideTarget ? (
        <RatingOverrideDialog
          open={overrideTarget !== null}
          sport={overrideTarget.sport as SportName}
          currentValue={overrideTarget.effectiveSkillValue}
          onOverride={handleOverride}
          onClose={() => setOverrideTarget(null)}
          errorMessage={overrideError}
        />
      ) : null}
    </div>
  );
}

export default AdminUserSkillRatingsPage;
